import React from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { IParticipant, reviewService } from '../service/review.service';

interface ParticipantCardProps {
  participant: IParticipant;
  isYou?: boolean;
  queueIndex?: number;
  slug?: string;
  estimatedMinutes?: number;
  privacyMode?: boolean;
}

const getInitials = (name: string) => {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const getStatusConfig = (status: string, position: number, t: (key: string) => string) => {
  if (status === 'active') {
    return {
      label: t('participantCard.inMeeting'),
      color: 'bg-green-500',
      textColor: 'text-green-700'
    };
  }
  if (status === 'done') {
    return { label: t('participantCard.done'), color: 'bg-gray-400', textColor: 'text-gray-700' };
  }
  if (position === 0) {
    return {
      label: t('participantCard.nextUp'),
      color: 'bg-orange-400',
      textColor: 'text-orange-800'
    };
  }
  if (position === 1) {
    return {
      label: t('participantCard.onStandby'),
      color: 'bg-yellow-400',
      textColor: 'text-yellow-700'
    };
  }
  return { label: t('participantCard.waiting'), color: 'bg-gray-300', textColor: 'text-gray-600' };
};

const WaitCountdown = ({ estimatedMinutes }: { estimatedMinutes: number }) => {
  const [secondsLeft, setSecondsLeft] = React.useState(estimatedMinutes * 60);
  const startRef = React.useRef(Date.now());
  const initialRef = React.useRef(estimatedMinutes * 60);

  React.useEffect(() => {
    startRef.current = Date.now();
    initialRef.current = estimatedMinutes * 60;
    setSecondsLeft(estimatedMinutes * 60);
  }, [estimatedMinutes]);

  React.useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startRef.current) / 1000);
      setSecondsLeft(Math.max(0, initialRef.current - elapsed));
    }, 1000);
    return () => clearInterval(interval);
  }, [estimatedMinutes]);

  if (secondsLeft <= 0) return <span>any moment</span>;
  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  if (mins === 0) return <span>{secs}s</span>;
  return <span>{mins}:{secs.toString().padStart(2, '0')}</span>;
};

export const ParticipantCard = ({ participant, isYou, queueIndex, slug, estimatedMinutes, privacyMode }: ParticipantCardProps) => {
  const { t } = useTranslation();
  const { name, position, status } = participant;
  const displayIndex = queueIndex ?? position;
  const initials = getInitials(name);
  const statusConfig = getStatusConfig(status, displayIndex, t);
  const isActive = status === 'active';

  const [editing, setEditing] = React.useState(false);
  const [editName, setEditName] = React.useState(name);
  const [saving, setSaving] = React.useState(false);
  const [nameTakenModal, setNameTakenModal] = React.useState<{ name: string; suggestions: string[] } | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  React.useEffect(() => {
    if (!editing) setEditName(name);
  }, [name, editing]);

  const doRename = async (newName: string) => {
    if (!slug) return;
    setSaving(true);
    try {
      await reviewService.renameParticipant(slug, participant.id, newName);
      setEditing(false);
      setNameTakenModal(null);
    } catch (err: any) {
      const data = err.response?.data;
      if (data?.code === 'NAME_TAKEN' && Array.isArray(data.suggestions)) {
        setNameTakenModal({ name: newName, suggestions: data.suggestions });
      } else {
        toast.error(t('participantCard.renameFailed'));
        setEditName(name);
        setEditing(false);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === name) {
      setEditing(false);
      setEditName(name);
      return;
    }
    doRename(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setEditName(name);
      setEditing(false);
    }
  };

  const handleSuggestionPick = (suggestion: string) => {
    setEditName(suggestion);
    doRename(suggestion);
  };

  return (
    <>
      <div
        className={`flex items-center gap-[12px] p-[14px_16px] rounded-[16px] border transition-all duration-200
          ${
            isActive
              ? 'border-[#1D1D1E] bg-[#F5F5F6] shadow-[0_4px_20px_rgba(0,0,0,0.1)]'
              : isYou
                ? 'border-[#BFBFC0] bg-[#F5F5F6] shadow-[0_2px_10px_rgba(42,60,68,0.08)]'
                : 'border-[rgba(0,0,0,0.11)] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.07),0_1px_2px_rgba(0,0,0,0.05)]'
          }
          ${status === 'done' ? 'opacity-60' : ''}
        `}
      >
        <span className="flex items-center justify-center w-[32px] h-[32px] rounded-full bg-[#1D1D1E] text-white text-[12px] font-semibold leading-none flex-shrink-0">
          {privacyMode ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/>
            </svg>
          ) : initials}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-[6px]">
            {privacyMode ? (
              <span className="block h-[16px] w-[120px] rounded-[6px] bg-[#BFBFC0]" />
            ) : isYou && editing ? (
              <input
                ref={inputRef}
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
                disabled={saving}
                maxLength={50}
                className="font-body text-[16px] font-semibold text-[#111827] bg-white border border-[#BFBFC0] rounded-[8px] px-[8px] py-[2px] w-full min-w-0 outline-none focus:border-[#0CB3D8] focus:shadow-[0_0_0_3px_rgba(93,225,255,0.20)] transition-all"
              />
            ) : (
              <>
                <span className="font-body text-[16px] font-semibold text-[#111827] truncate">
                  {name}
                </span>
                {isYou && slug && (
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="flex-shrink-0 p-[2px] rounded-[4px] text-[#818185] hover:text-[#1D1D1E] hover:bg-[#E5E5ED] transition-all"
                    title={t('participantCard.rename')}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                      <path d="m15 5 4 4" />
                    </svg>
                  </button>
                )}
              </>
            )}
            {isYou && !editing && (
              <span className="text-[11px] font-medium text-[#4A4A4C] bg-[#EFEFF1] px-[6px] py-[1px] rounded-[4px] flex-shrink-0">
                {t('participantCard.you')}
              </span>
            )}
          </div>
        </div>

        {status === 'waiting' && (
          <div className="flex flex-col items-end flex-shrink-0">
            <span className="font-mono text-[14px] font-medium text-gray-700">
              #{displayIndex + 1}
            </span>
            {estimatedMinutes != null && estimatedMinutes > 0 && (
              <span className="font-mono text-[10px] text-[#818185]">
                ~<WaitCountdown estimatedMinutes={estimatedMinutes} />
              </span>
            )}
          </div>
        )}

        <span
          className={`flex-shrink-0 text-[11px] font-semibold px-[8px] py-[3px] rounded-[5px] min-w-[85px] text-center ${statusConfig.color} ${statusConfig.textColor}`}
        >
          {statusConfig.label}
        </span>
      </div>

      {nameTakenModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[3px] px-[16px]"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              setNameTakenModal(null);
              setEditName(name);
              setEditing(false);
            }
          }}
        >
          <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] shadow-[0_20px_60px_rgba(0,0,0,0.18)] w-full max-w-[380px] p-[24px] flex flex-col gap-[16px]">
            <div>
              <h2 className="font-display text-[20px] font-bold text-base-black mb-[4px]">
                {t('participantCard.nameTakenTitle')}
              </h2>
              <p className="font-body text-[14px] text-gray-900 dark:text-[#A0A0A3] leading-[1.5]">
                {t('participantCard.nameTakenMessage', { name: nameTakenModal.name })}
              </p>
            </div>

            <p className="font-body text-[12px] text-[#818185] dark:text-[#A0A0A3] font-medium">
              {t('participantCard.nameTakenSuggestions')}
            </p>
            <div className="flex flex-col gap-[8px]">
              {nameTakenModal.suggestions.map((s, i) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => handleSuggestionPick(s)}
                  disabled={saving}
                  className={`w-full h-[44px] rounded-[12px] border font-body text-[14px] text-left px-[16px] disabled:opacity-50 transition-all ${
                    i === 0
                      ? 'border-[#0CB3D8] bg-[#F8FCFF] dark:bg-[#0ea5c9]/10 font-medium text-base-black dark:text-white hover:bg-[#EDF9FF] dark:hover:bg-[#0ea5c9]/15'
                      : 'border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] bg-[#F5F5F6] dark:bg-[#28282B] font-normal text-base-black dark:text-[#A0A0A3] hover:border-[#0CB3D8] hover:bg-[#F8FCFF] dark:hover:bg-[#0ea5c9]/10 dark:hover:text-white'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>

            <div className="border-t border-[#E5E5ED] dark:border-[rgba(255,255,255,0.08)] pt-[12px]">
              <p className="font-body text-[12px] text-[#818185] dark:text-[#A0A0A3] font-medium mb-[8px]">
                {t('participantCard.nameTakenCustom')}
              </p>
              <form
                className="flex gap-[8px] items-center"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (editName.trim() && editName.trim() !== nameTakenModal.name) {
                    doRename(editName.trim());
                  }
                }}
              >
                <input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  maxLength={50}
                  placeholder={t('participantCard.nameTakenPlaceholder')}
                  className="flex-1 min-w-0 h-[40px] px-[14px] text-[14px] font-body text-base-black bg-white dark:bg-[#28282B] border border-gray-700 dark:border-[rgba(255,255,255,0.12)] rounded-[24px] placeholder-gray-900 dark:placeholder-[#6B6B6F] focus:outline-none focus:border-[#0CB3D8] focus:shadow-[0_0_0_3px_rgba(93,225,255,0.20)] transition-all"
                />
                <button
                  type="submit"
                  disabled={saving || !editName.trim() || editName.trim() === nameTakenModal.name}
                  className={`h-[40px] px-[16px] rounded-[32px] font-body text-[13px] font-medium text-white transition-all flex-shrink-0 ${
                    saving || !editName.trim() || editName.trim() === nameTakenModal.name
                      ? 'bg-gray-900 opacity-50'
                      : 'bg-base-black dark:bg-[#C8C8CB] dark:text-[#1C1C1F] hover:bg-[#29292A] dark:hover:bg-[#D8D8DB]'
                  }`}
                >
                  {t('participantCard.rename')}
                </button>
              </form>
            </div>

            <button
              type="button"
              onClick={() => {
                setNameTakenModal(null);
                setEditName(name);
                setEditing(false);
              }}
              className="w-full h-[44px] rounded-[32px] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] bg-gray-400 dark:bg-[#28282B] font-body text-[14px] font-medium text-base-black hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </>
  );
};
